/**
 * Modal canônico de criar/editar responsável (ac-modal-dual).
 * Usado na lista de Responsáveis e na aba Responsável da ficha do aluno.
 */
import { useEffect, useRef, useState } from 'react';
import { User, Camera, GraduationCap, Users, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '../../components/ui/Modal';
import { AvatarCropper } from '../../components/ui/AvatarCropper';
import { ListAvatar, hasFotoReal } from '../../components/admin/list-avatar';
import { validateCPF, formatCPF, formatPhone, stripMask } from '../../utils/cpfUtils';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import '../../components/ui/datatable.css';

export const RESPONSAVEL_PARENTESCOS = ['Pai', 'Mãe', 'Avô/Avó', 'Tio/Tia', 'Outro'] as const;

export type ResponsavelFormAlunoVinculo = {
  aluno: {
    id: string;
    nome: string;
    matricula?: string;
    fotoUrl?: string | null;
    turma?: { id: string; nome: string; serie?: string; turno?: string } | null;
  };
  grauParentesco?: string;
  principal?: boolean;
};

export type ResponsavelFormModel = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  fotoUrl?: string | null;
  cpf?: string;
  parentesco?: string;
  dataNascimento?: string;
  usuarioId?: string | null;
  alunos?: ResponsavelFormAlunoVinculo[];
};

type FormState = {
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  parentesco: string;
  dataNascimento: string;
};

function emptyForm(): FormState {
  return { nome: '', email: '', telefone: '', cpf: '', parentesco: '', dataNascimento: '' };
}

function formFromResponsavel(r: ResponsavelFormModel): FormState {
  return {
    nome: r.nome || '',
    email: r.email || '',
    telefone: formatPhone(r.telefone || ''),
    cpf: formatCPF(r.cpf || ''),
    parentesco: r.parentesco || '',
    dataNascimento: r.dataNascimento
      ? r.dataNascimento.split(/[T ]/)[0]
      : '',
  };
}

function extractErrorMessage(err: unknown): string {
  const ax = err as { response?: { data?: { error?: { message?: string }; message?: string | string[] } } };
  const resp = ax?.response?.data;
  if (resp?.error?.message) return resp.error.message;
  if (Array.isArray(resp?.message)) return resp.message.join('. ');
  if (typeof resp?.message === 'string') return resp.message;
  return 'Erro ao salvar. Tente novamente.';
}

type Props = {
  open: boolean;
  onClose: () => void;
  escolaId: string;
  /** null = novo cadastro */
  responsavel: ResponsavelFormModel | null;
  /** Chamado após salvar com sucesso */
  onSaved?: (result: { id: string; created: boolean; responsavel?: ResponsavelFormModel }) => void;
  onMatricularNovoAluno?: (responsavel: ResponsavelFormModel) => void;
};

export function ResponsavelFormModal({
  open,
  onClose,
  escolaId,
  responsavel,
  onSaved,
  onMatricularNovoAluno,
}: Props) {
  const editing = responsavel;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [fotoPendente, setFotoPendente] = useState<string | null>(null);
  const [fotoUploading, setFotoUploading] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const [alunos, setAlunos] = useState<ResponsavelFormAlunoVinculo[]>([]);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? formFromResponsavel(editing) : emptyForm());
    setFormErrors({});
    setFotoUrl(editing?.fotoUrl ?? null);
    setFotoPendente(null);
    setFotoUploading(false);
    setAvatarCropFile(null);
    setAlunos(editing?.alunos ?? []);
  }, [open, editing]);

  const resetPhoto = () => {
    setAvatarCropFile(null);
    setFotoPendente(null);
    setFotoUploading(false);
  };

  const handleClose = () => {
    resetPhoto();
    onClose();
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.nome.trim()) {
      errors.nome = 'Nome é obrigatório.';
    } else if (form.nome.trim().length < 3) {
      errors.nome = 'Nome deve ter pelo menos 3 caracteres.';
    }
    const telDigits = stripMask(form.telefone);
    if (!telDigits) {
      errors.telefone = 'Telefone é obrigatório.';
    } else if (telDigits.length < 10 || telDigits.length > 11) {
      errors.telefone = 'Telefone inválido. Use DDD + número (10 ou 11 dígitos).';
    }
    if (!form.email.trim()) {
      errors.email = 'E-mail é obrigatório.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = 'E-mail inválido.';
    }
    if (!form.cpf.trim()) {
      errors.cpf = 'CPF é obrigatório.';
    } else if (!validateCPF(form.cpf)) {
      errors.cpf = 'CPF inválido. Verifique os dígitos.';
    }
    if (!form.dataNascimento) {
      errors.dataNascimento = 'Data de nascimento é obrigatória.';
    } else {
      const birth = new Date(form.dataNascimento);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      if (age < 18) {
        errors.dataNascimento = 'O responsável deve ter pelo menos 18 anos.';
      }
    }
    if (!form.parentesco) {
      errors.parentesco = 'Parentesco é obrigatório.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkCpfLive = async (cpfVal: string) => {
    const cleanCpf = stripMask(cpfVal);
    if (cleanCpf.length === 11 && validateCPF(cpfVal)) {
      try {
        const { data } = await api.get(ROUTES.validacoes.cpf(cleanCpf));
        if (data.exists) {
          if (editing && data.nome === editing.nome) return;
          const msg = `Este CPF já está em uso pelo ${
            data.tipo === 'RESPONSAVEL'
              ? 'Responsável'
              : data.tipo === 'ALUNO'
                ? 'Aluno'
                : 'Usuário'
          }: ${data.nome}.`;
          setFormErrors((prev) => ({ ...prev, cpf: msg }));
          toast.error(msg);
        }
      } catch (err) {
        console.error('Erro validando CPF', err);
      }
    }
  };

  const onAvatarFilePicked: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx. 5 MB).');
      return;
    }
    setAvatarCropFile(f);
  };

  const onAvatarCropped = async (fotoBase64: string) => {
    setAvatarCropFile(null);
    if (editing?.id) {
      setFotoUploading(true);
      try {
        const { data } = await api.post<{ success: boolean; url: string }>(
          ROUTES.upload.fotoResponsavel(editing.id),
          { fotoBase64 },
        );
        setFotoUrl(data.url);
        toast.success('Foto atualizada!');
      } catch {
        toast.error('Não foi possível enviar a foto.');
      } finally {
        setFotoUploading(false);
      }
    } else {
      setFotoPendente(fotoBase64);
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !escolaId) return;
    setSaving(true);
    try {
      if (editing) {
        await api.patch(ROUTES.responsaveis.findOne(escolaId, editing.id), form);
        toast.success('Responsável atualizado!');
        resetPhoto();
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
        onSaved?.({ id: editing.id, created: false });
        onClose();
      } else {
        const { data: created } = await api.post<ResponsavelFormModel>(
          ROUTES.responsaveis.listar(escolaId),
          form,
        );
        if (fotoPendente && created?.id) {
          try {
            await api.post(ROUTES.upload.fotoResponsavel(created.id), {
              fotoBase64: fotoPendente,
            });
            toast.success('Responsável cadastrado com foto!');
          } catch {
            toast.error('Cadastro salvo; não foi possível enviar a foto.');
          }
        } else {
          toast.success('Responsável cadastrado!');
        }
        resetPhoto();
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
        onSaved?.({ id: created.id, created: true, responsavel: created });
        onClose();
      }
    } catch (err) {
      const msg = extractErrorMessage(err);
      toast.error(msg);
      const msgLower = msg.toLowerCase();
      if (msgLower.includes('email') || msgLower.includes('e-mail')) {
        setFormErrors((prev) => ({ ...prev, email: msg }));
      } else if (msgLower.includes('telefone')) {
        setFormErrors((prev) => ({ ...prev, telefone: msg }));
      } else if (msgLower.includes('cpf')) {
        setFormErrors((prev) => ({ ...prev, cpf: msg }));
      }
    } finally {
      setSaving(false);
    }
  };

  const modalFotoSrc = fotoPendente || fotoUrl || null;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={editing ? 'Editar Responsável' : 'Novo Responsável'}
        contentClassName="ac-responsavel-modal"
        preventClose
        dialogId="modal-novo-aluno"
        footer={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 8 }}>
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Cancelar
            </button>
            <button
              type="button"
              data-testid="btn-salvar-responsavel"
              className="btn-save"
              onClick={() => void handleSave()}
              disabled={saving || fotoUploading}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        )}
      >
        <img
          src="/icons/logo-shield.svg"
          alt="Aluno Conectado"
          style={{
            position: 'absolute',
            top: 6,
            right: 10,
            width: 26,
            height: 26,
            opacity: 0.4,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
        <div className="ac-modal-dual">
          <div className="ac-modal-dual__photo ac-responsavel-modal-photo">
            <button
              type="button"
              className={`ac-responsavel-avatar-btn ${fotoUploading ? 'ac-responsavel-avatar-btn--busy' : ''}`}
              disabled={fotoUploading || saving}
              onClick={() => {
                if (!fotoUploading) avatarFileInputRef.current?.click();
              }}
              title="Alterar foto"
            >
              {hasFotoReal(modalFotoSrc) ? (
                <img src={modalFotoSrc!} alt="" />
              ) : (
                <span className="ac-responsavel-avatar-placeholder">
                  <User size={22} strokeWidth={1.5} />
                </span>
              )}
              <span className="ac-responsavel-avatar-cam" aria-hidden>
                <Camera size={11} strokeWidth={2} />
              </span>
            </button>
            <input
              ref={avatarFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="ac-responsavel-avatar-file-input"
              onChange={onAvatarFilePicked}
              tabIndex={-1}
              aria-hidden
            />
            <div className="ac-responsavel-modal-photo-hint">
              <strong>Foto do responsável</strong>
              <span>
                {editing
                  ? 'Toque na foto para escolher uma imagem (JPEG, PNG ou WebP, até 5 MB).'
                  : 'Opcional. Você pode enviar antes de salvar; a foto será gravada junto com o cadastro.'}
              </span>
            </div>
          </div>

          <div className="ac-modal-dual__columns">
            <div className="ac-modal-dual__main ac-responsavel-modal-left">
              <div className="ac-responsavel-modal-fields">
                <div className="form-group">
                  <label className="form-label" htmlFor="resp-nome">
                    Nome completo *
                  </label>
                  <input
                    id="resp-nome"
                    className={`form-input${formErrors.nome ? ' form-input--error' : ''}`}
                    value={form.nome}
                    onChange={(e) => {
                      setForm({ ...form, nome: e.target.value });
                      setFormErrors((prev) => ({ ...prev, nome: '' }));
                    }}
                    placeholder="Nome do responsável"
                    autoComplete="name"
                  />
                  {formErrors.nome && <span className="form-error-text">{formErrors.nome}</span>}
                </div>
                <div className="ac-responsavel-modal-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="resp-email">
                      E-mail *
                    </label>
                    <input
                      id="resp-email"
                      className={`form-input${formErrors.email ? ' form-input--error' : ''}`}
                      type="email"
                      value={form.email}
                      onChange={(e) => {
                        setForm({ ...form, email: e.target.value });
                        setFormErrors((prev) => ({ ...prev, email: '' }));
                      }}
                      placeholder="email@exemplo.com"
                      autoComplete="email"
                      readOnly={!!(editing && editing.usuarioId)}
                      disabled={!!(editing && editing.usuarioId)}
                    />
                    {editing && editing.usuarioId && (
                      <span style={{ fontSize: '0.56rem', color: '#f59e0b', marginTop: 2 }}>
                        Conta ativa — e-mail bloqueado (usado para login).
                      </span>
                    )}
                    {formErrors.email && <span className="form-error-text">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="resp-tel">
                      Telefone *
                    </label>
                    <input
                      id="resp-tel"
                      className={`form-input${formErrors.telefone ? ' form-input--error' : ''}`}
                      value={form.telefone}
                      onChange={(e) => {
                        setForm({ ...form, telefone: formatPhone(e.target.value) });
                        setFormErrors((prev) => ({ ...prev, telefone: '' }));
                      }}
                      placeholder="(00) 00000-0000"
                      autoComplete="tel"
                    />
                    {formErrors.telefone && (
                      <span className="form-error-text">{formErrors.telefone}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="resp-cpf">
                      CPF *
                    </label>
                    <input
                      id="resp-cpf"
                      className={`form-input${formErrors.cpf ? ' form-input--error' : ''}`}
                      value={form.cpf}
                      onChange={(e) => {
                        setForm({ ...form, cpf: formatCPF(e.target.value) });
                        setFormErrors((prev) => ({ ...prev, cpf: '' }));
                      }}
                      onBlur={() => void checkCpfLive(form.cpf)}
                      placeholder="000.000.000-00"
                    />
                    {formErrors.cpf && <span className="form-error-text">{formErrors.cpf}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="resp-nasc">
                      Data de Nascimento *
                    </label>
                    <input
                      id="resp-nasc"
                      className={`form-input${formErrors.dataNascimento ? ' form-input--error' : ''}`}
                      type="date"
                      value={form.dataNascimento}
                      onChange={(e) => {
                        setForm({ ...form, dataNascimento: e.target.value });
                        setFormErrors((prev) => ({ ...prev, dataNascimento: '' }));
                      }}
                    />
                    {formErrors.dataNascimento && (
                      <span className="form-error-text">{formErrors.dataNascimento}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="resp-parentesco">
                      Parentesco *
                    </label>
                    <select
                      id="resp-parentesco"
                      className={`form-select${formErrors.parentesco ? ' form-input--error' : ''}`}
                      value={form.parentesco}
                      onChange={(e) => {
                        setForm({ ...form, parentesco: e.target.value });
                        setFormErrors((prev) => ({ ...prev, parentesco: '' }));
                      }}
                    >
                      <option value="">Selecione…</option>
                      {RESPONSAVEL_PARENTESCOS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                    {formErrors.parentesco && (
                      <span className="form-error-text">{formErrors.parentesco}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="ac-modal-dual__divider ac-responsavel-modal-divider" aria-hidden />

            <div className="ac-modal-dual__side ac-responsavel-modal-right">
              <div className="ac-modal-dual__side-panel">
                <div className="ac-modal-dual__side-head ac-responsavel-alunos-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <GraduationCap size={14} strokeWidth={1.5} />
                    <strong>Alunos vinculados</strong>
                  </div>
                  {editing && onMatricularNovoAluno && (
                    <button
                      type="button"
                      onClick={() => onMatricularNovoAluno(editing)}
                      style={{ fontSize: '0.7rem', padding: '3px 8px', background: 'var(--bg-muted, #f3f4f6)', color: 'var(--primary-color, #6366f1)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                    >
                      <Plus size={11} /> Matricular Aluno
                    </button>
                  )}
                </div>
                <div className="ac-modal-dual__side-body">
                  {alunos.length > 0 ? (
                    <ul className="ac-responsavel-alunos-list">
                      {alunos.map((v) => (
                        <li key={v.aluno.id} className="ac-responsavel-aluno-item">
                          <ListAvatar fotoUrl={v.aluno.fotoUrl} nome={v.aluno.nome} size={32} />
                          <div className="ac-responsavel-aluno-info">
                            <span className="ac-responsavel-aluno-nome">{v.aluno.nome}</span>
                            <span className="ac-responsavel-aluno-turma">
                              {v.aluno.turma
                                ? `${v.aluno.turma.nome}${v.aluno.turma.turno ? ` • ${v.aluno.turma.turno}` : ''}`
                                : 'Sem turma'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="ac-responsavel-alunos-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Users size={22} strokeWidth={1.5} className="ac-responsavel-alunos-empty-icon" />
                      <span>Nenhum aluno vinculado.</span>
                      <span className="ac-responsavel-alunos-empty-hint" style={{ marginBottom: 12 }}>
                        {editing
                          ? 'Use a ação "Vincular Aluno" na listagem.'
                          : 'Salve o cadastro primeiro, depois vincule alunos.'}
                      </span>
                      {editing && onMatricularNovoAluno && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => onMatricularNovoAluno(editing)}
                          style={{ fontSize: '0.78rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <Plus size={14} /> Matricular Novo Aluno
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {avatarCropFile && (
        <AvatarCropper
          file={avatarCropFile}
          onCropped={(b64) => void onAvatarCropped(b64)}
          onCancel={() => setAvatarCropFile(null)}
        />
      )}
    </>
  );
}
